import { useState, useCallback } from 'react';
import { authFetch, API_URL } from './useAuth';

export function useStoreData(apiKey) {
  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [storesMeta, setStoresMeta] = useState({ stores_used: 0, stores_limit: 1, can_add: false, tier: 'free' });
  const [showAddStore, setShowAddStore] = useState(false);
  const [newStoreLabel, setNewStoreLabel] = useState('');
  const [newStoreName, setNewStoreName] = useState('');
  const [newStoreVat, setNewStoreVat] = useState('');
  const [newStoreReg, setNewStoreReg] = useState('');
  const [newStoreStreet, setNewStoreStreet] = useState('');
  const [newStoreCity, setNewStoreCity] = useState('');
  const [newStorePostcode, setNewStorePostcode] = useState('');
  const [newStoreCountry, setNewStoreCountry] = useState('');
  const [newStoreBilling, setNewStoreBilling] = useState('');
  const [storeLoading, setStoreLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  const fetchStores = useCallback(async () => {
    if (!apiKey) return;
    try {
      const res = await authFetch(`${API_URL}/api/credentials/stores`);
      if (res.ok) {
        const d = await res.json();
        setStores(d.stores || []);
        setStoresMeta({ stores_used: d.stores_used, stores_limit: d.stores_limit, can_add: d.can_add, tier: d.tier || 'free' });
        // Do NOT auto-select — pipeline works with user_credentials when no store is selected
      }
    } catch (err) { console.error('Failed to fetch stores:', err); }
  }, [apiKey]);

  const handleAddStore = async (onMessage) => {
    if (!newStoreLabel) return;
    setStoreLoading(true);
    try {
      const res = await authFetch(`${API_URL}/api/credentials/stores`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          store_label: newStoreLabel,
          store_name: newStoreName || undefined,
          vat_number: newStoreVat || undefined,
          reg_number: newStoreReg || undefined,
          address_street: newStoreStreet || undefined,
          address_city: newStoreCity || undefined,
          address_postcode: newStorePostcode || undefined,
          address_country: newStoreCountry || undefined,
          billing_account: newStoreBilling || undefined
        })
      });
      const d = await res.json();
      if (res.ok && d.success) {
        setShowAddStore(false);
        setNewStoreLabel(''); setNewStoreName(''); setNewStoreVat('');
        setNewStoreReg(''); setNewStoreStreet(''); setNewStoreCity('');
        setNewStorePostcode(''); setNewStoreCountry(''); setNewStoreBilling('');
        fetchStores();
        return { success: true };
      } else {
        if (onMessage) onMessage(d.message || d.error || 'Failed to add store');
        return { success: false };
      }
    } catch (err) {
      console.error('Failed to add store:', err);
      if (onMessage) onMessage('Failed to add store');
      return { success: false };
    } finally { setStoreLoading(false); }
  };

  const handleDeleteStore = async (storeId) => {
    try {
      const res = await authFetch(`${API_URL}/api/credentials/stores/${storeId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        setShowDeleteConfirm(null);
        if (selectedStoreId === storeId) setSelectedStoreId('');
        fetchStores();
        return { success: true };
      }
    } catch (err) { console.error('Failed to delete store:', err); }
    return { success: false };
  };

  return {
    stores, selectedStoreId, setSelectedStoreId,
    storesMeta, showAddStore, setShowAddStore,
    newStoreLabel, setNewStoreLabel,
    newStoreName, setNewStoreName,
    newStoreVat, setNewStoreVat,
    newStoreReg, setNewStoreReg,
    newStoreStreet, setNewStoreStreet,
    newStoreCity, setNewStoreCity,
    newStorePostcode, setNewStorePostcode,
    newStoreCountry, setNewStoreCountry,
    newStoreBilling, setNewStoreBilling,
    storeLoading, showDeleteConfirm, setShowDeleteConfirm,
    fetchStores, handleAddStore, handleDeleteStore,
  };
}
